package top.wcpe.allincore.dsl.ir.canvas

import kotlin.jvm.JvmStatic

/**
 * draw 操作数 IR：字面量 double / ExprBlob 字节流 / UTF-8 字符串。
 *
 * 从原 `DrawBlob.Operand` 顶级化拆出（M11.x 重构）；语义与字段恒定，
 * 仅是外部访问路径由 `DrawBlob.Operand.*` 变为 `Operand.*`。
 */
sealed class Operand {

    abstract fun isLiteral(): Boolean

    companion object {
        /** double 字面量操作数。 */
        @JvmStatic
        fun literal(value: Double): Operand = Literal(value)

        /** ExprBlob 操作数；调用方提供已序列化的字节流。 */
        @JvmStatic
        fun expr(exprBytes: ByteArray): Operand = Expr(exprBytes)

        /** UTF-8 字符串操作数（M11.4，用于 TEXT.content / DRAW_IMAGE.resourceId）。 */
        @JvmStatic
        fun str(value: String): Operand = Str(value)

        /**
         * v4 B.5：byte[] 字节数组操作数（OP_BITMAP.data 专用）。
         *
         * 仿 [str] 的 LP 模式，但保留原 byte 不做 UTF-8 编解码。
         */
        @JvmStatic
        fun bytes(value: ByteArray): Operand = Bytes(value)
    }

    /** double 字面量操作数。 */
    class Literal internal constructor(val value: Double) : Operand() {

        override fun isLiteral(): Boolean = true

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is Literal) return false
            return value.compareTo(other.value) == 0
        }

        override fun hashCode(): Int = value.hashCode()
    }

    class Expr internal constructor(bytes: ByteArray) : Operand() {

        private val bytes: ByteArray

        init {
            require(bytes.size <= 0xFFFF) { "ExprBlob 字节超过 65535: ${bytes.size}" }
            this.bytes = bytes.copyOf()
        }

        /** 返回字节数组的拷贝。 */
        fun getBytes(): ByteArray = bytes.copyOf()

        /** 返回 ExprBlob 字节长度（避免拷贝开销）。 */
        fun getByteLength(): Int = bytes.size

        override fun isLiteral(): Boolean = false

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is Expr) return false
            return bytes.contentEquals(other.bytes)
        }

        override fun hashCode(): Int = bytes.contentHashCode()
    }

    /** UTF-8 字符串操作数：u8 kind(0x02) + u16 byteLen + bytes。 */
    class Str internal constructor(val value: String) : Operand() {

        init {
            val bytes = value.encodeToByteArray()
            require(bytes.size <= 0xFFFF) { "字符串 UTF-8 字节超过 65535: ${bytes.size}" }
        }

        override fun isLiteral(): Boolean = false

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is Str) return false
            return value == other.value
        }

        override fun hashCode(): Int = value.hashCode()
    }

    /**
     * v4 B.5：byte[] 字节数组操作数（OP_BITMAP.data 专用）。
     *
     * 布局：u8 kind(0x03) + u16 byteLen + raw bytes。
     * 构造期对入参做防御性拷贝；[getValue] 同样返回拷贝，保证不可变语义。
     */
    class Bytes internal constructor(value: ByteArray) : Operand() {

        private val raw: ByteArray

        init {
            require(value.size <= 0xFFFF) { "byte 数组长度超过 65535: ${value.size}" }
            this.raw = value.copyOf()
        }

        /** byte 数组的拷贝（防御性复制；属性形态，Java 侧仍为 getValue()）。 */
        val value: ByteArray
            get() = raw.copyOf()

        /** 返回 byte 数组长度（避免拷贝开销）。 */
        fun getByteLength(): Int = raw.size

        override fun isLiteral(): Boolean = false

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is Bytes) return false
            return raw.contentEquals(other.raw)
        }

        override fun hashCode(): Int = raw.contentHashCode()
    }
}
